// The Calendar board's small shared pieces: an event's color, which events show (the board's own and its Google
// calendars') and whether one is over, and an event's menu from a right-click.

import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { CalendarDays, ExternalLink, SquareKanban, Trash2 } from "lucide-react";
import { BOARD_CAL, LIST_DAYS, calSetOf, eventOf, eventTitle, isEvent, monthDays, shownFrom, showingsOf, type AgendaView, type EventData, type Shown } from "../../../shared/agenda";
import { boardEventShows, boardShows, calCss, copiesOf, isLinkedCopy, linkedCopies, oneOfEachHoliday, type CalColors, type GcalCalendar, type GcalEvent } from "../../../shared/gcal";
import { joinIn, type Join } from "../../../shared/meet";
import { layerBoards, layerShowings, type LayerKind } from "../../../shared/layers";
import { addDays } from "../../../shared/days";
import type { Entry, ID } from "../../../shared/types";
import { readEvents, useGcal, useGcalPending } from "../../state/gcal";
import { get, useStore } from "../../state/store";
import { Pop } from "../calendar/Pop";
import { zoneHere } from "./actions";
import { canChange, defaultCalendars, deleteShown } from "./edit";
import { watchCopies } from "./copies";
import { eventMenu, openEvent, startsOn, useAgendaUi, type Draft } from "./state";
import { pad } from "../../../shared/repeat";

/** An event's color, as a style: its own named color, else its calendar's (the board's own is the theme's accent). */
export function evStyle(ev: Pick<EventData, "color">): React.CSSProperties {
  return { "--ev": ev.color ? `var(--tc-${ev.color})` : "var(--accent)" } as React.CSSProperties;
}

/** A shown event's color: a Google event's own, else as evStyle. */
export function shownStyle(s: Pick<Shown, "color" | "ev" | "meta">): React.CSSProperties {
  // an event in two Google calendars shows both colours (a second one: --ev2, drawn beside the first in agenda.css)
  return s.color ? ({ "--ev": s.color, ...(s.meta?.mark2 ? { "--ev2": s.meta.mark2 } : {}) } as React.CSSProperties) : evStyle(s.ev);
}

/** The colours the user gave calendars (Fixes 11), read again when they change. */
export const useCalColors = (): CalColors | undefined => useStore((s) => s.workspace?.calColors ?? undefined);

/** A Google calendar's color as CSS: the one the user gave it (a theme colour follows the theme), else as Google gives it. */
export const calColor = (c: Pick<GcalCalendar, "key" | "color">, picks: CalColors | undefined = get().workspace?.calColors ?? undefined) => calCss(picks?.[c.key], c.color);

/** A calendar's own color: a Google one's; null for the board's own, which is the theme's accent. */
export function calendarColor(key: string): string | null {
  if (key === BOARD_CAL) return null;
  for (const a of useGcal.getState().status?.accounts ?? []) for (const c of a.calendars) if (c.key === key) return calColor(c);
  return null;
}

/** A new event's color while it is made: its own, else the first Google calendar it goes in. */
export function draftStyle(d: Pick<Draft, "color" | "cals" | "boardId">): React.CSSProperties {
  if (d.color) return evStyle(d);
  const cals = d.cals ?? defaultCalendars(d.boardId);
  const c = cals.length ? calendarColor(cals[0]) : null;
  return c ? ({ "--ev": c } as React.CSSProperties) : evStyle({});
}

/** Whether an event is one of the board's own, which the board changes; a Google one is read only until its edits come. */
export const isOwn = (s: Pick<Shown, "cal">) => s.cal === BOARD_CAL;

/** What a Google event shows besides its times and words: its link, repeat, tentative state and meeting (step 5). */
export function metaOf(g: GcalEvent): NonNullable<Shown["meta"]> {
  return { ...(g.link ? { link: g.link } : {}), ...(g.recurring ? { recurring: true } : {}), ...(g.status === "tentative" ? { tentative: true } : {}), ...(g.join ? { join: g.join } : {}), ...(g.guests ? { guests: g.guests } : {}), ...(g.you ? { you: g.you } : {}), ...(g.from ? { from: g.from } : {}) };
}

/** A layer's icon (step 6): its board's own (the content calendar's or a pipeline's), as kinds.ts gives each kind. */
const LAYER_ICON = { post: CalendarDays, followup: SquareKanban };
export function LayerIcon({ kind, className }: { kind: LayerKind; className: string }) {
  const Icon = LAYER_ICON[kind];
  return <Icon size={11} className={className} aria-hidden />;
}

/** A layer item's mark: its board's icon, before its title. */
export function LayerMark({ s }: { s: Pick<Shown, "meta"> }) {
  const layer = s.meta?.layer;
  return layer ? <LayerIcon kind={layer.kind} className="agd-layer-mark" /> : null;
}

/** What a layer item says after its title, where there is room: a post's channel and status, a follow-up's contact. */
export function LayerSub({ s }: { s: Pick<Shown, "meta"> }) {
  const sub = s.meta?.layer?.sub;
  return sub ? <span className="agd-layer-sub">{sub}</span> : null;
}

/** A layer item's words for a screen reader, after its title and time. */
export const layerWords = (s: Pick<Shown, "meta">) => (s.meta?.layer?.sub ? `, ${s.meta.layer.sub}` : "");

/** An event's call (step 5): Google's meeting details for a Google event, else a meeting link in its place or notes. */
export const joinFor = (s: Pick<Shown, "meta" | "ev">): Join | null => s.meta?.join ?? joinIn(s.ev.location) ?? joinIn(s.ev.notes);

/** The days a board reads its Google events for, around the day in view: the month's grid, a week either side, and all of the list's four weeks. */
export function readSpan(at: string, view?: AgendaView): [string, string] {
  const days = monthDays(at, startsOn);
  const to = addDays(days[days.length - 1], 7);
  const listTo = addDays(at, LIST_DAYS - 1);
  return [addDays(days[0], -7), view === "list" && listTo > to ? listTo : to];
}

/**
 * The events that show on a board, on this browser's clock: the board's own unless their calendar is hidden, and the
 * events of each Google calendar the board shows, in its color. What a calendar could not read is said, by calendar.
 */
export function useShownEvents(boardId: ID, entries: Entry[], at?: string, view?: AgendaView): { list: Shown[]; hidden: string[]; problems: { cal: string; error: string }[]; calendars: GcalCalendar[] } {
  const status = useGcal((s) => s.status);
  const read = useGcal((s) => s.events[boardId]);
  const pending = useGcalPending((s) => s.shown);
  const picks = useCalColors();
  // layers (step 6): the dated things on the user's content calendars and pipelines; a change on any other
  // board draws nothing again
  const boards = useStore((s) => s.boards);
  const layered = useMemo(() => new Set(layerBoards(boards).map((b) => b.id)), [boards]);
  const all = useStore(useShallow((s) => Object.values(s.entries).filter((e) => layered.has(e.boardId))));
  const span = at ? readSpan(at, view) : null;
  const connected = !!status?.accounts.length;
  // a change made to an event that has copies in Google reaches them (copies.ts)
  useEffect(() => watchCopies(), []);
  useEffect(() => {
    if (span && connected) void readEvents(boardId, span[0], span[1]);
    // the span's days are the read's key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, span?.[0], span?.[1], connected]);
  return useMemo(() => {
    const set = calSetOf(entries, boardId);
    const zone = zoneHere();
    const [a, b] = span ?? ["0000-01-01", "9999-12-31"];
    const calendars = status?.accounts.flatMap((a) => a.calendars) ?? [];
    const byKey = new Map(calendars.map((c) => [c.key, c]));
    // every event is on the board; one with copies in Google calendars shows while the board shows one of them, once, in
    // each one's colour, and the copies read from Google are left out (Fixes 11)
    const known = (k: string) => byKey.get(k) ?? null;
    const own = entries
      .filter(isEvent)
      .filter((e) => boardEventShows(set, Object.keys(eventOf(e).g ?? {}), known))
      .flatMap((e) => {
        return (span ? showingsOf(e, zone, a, b) : showingsOf(e, zone, a, a).slice(0, 1)).map((s) => withMarks(s, Object.keys(eventOf(e).g ?? {}), byKey, picks));
      });
    const linked = linkedCopies(entries);
    const copies = copiesOf(status?.accounts, (k, sel) => boardShows(set, k, sel));
    // what this board shows, then each holiday once: a holiday from a calendar not shown here never stands in for one that is
    const here = (read?.list ?? []).filter((g) => {
      const c = byKey.get(g.cal);
      return !!c && !copies.has(g.cal) && !isLinkedCopy(linked, g) && boardShows(set, g.cal, c.selected);
    });
    const google = oneOfEachHoliday(here, (cal) => byKey.get(cal)?.id).flatMap((g): Shown[] => {
      const c = byKey.get(g.cal)!;
      const id = `${g.cal}#${g.id}`;
      const wait = pending[id];
      if (wait?.hidden) return [];
      // a change let go on the board shows at once, until Google's answer replaces it
      const ev: EventData = { title: g.title, allDay: g.allDay, start: g.start, end: g.end, tz: g.tz, ...(g.location ? { location: g.location } : {}), ...(g.notes ? { notes: g.notes } : {}), ...(wait ? (Object.fromEntries(Object.entries(wait).filter(([k, v]) => v !== undefined && ["title", "allDay", "start", "end", "tz"].includes(k))) as Partial<EventData>) : {}) };
      const s = shownFrom(id, ev, zone, g.cal, g.color ?? calColor(c, picks));
      return [{ ...s, ...(g.series ? { series: g.series } : {}), ...(g.occ ? { occ: g.occ } : {}), meta: metaOf(g) }];
    });
    const layers = span ? layerShowings(boards, all, a, b, zone, set.hidden) : [];
    return { list: [...own, ...google, ...layers], hidden: set.hidden, problems: read?.problems ?? [], calendars };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, boardId, read, status, pending, picks, boards, all, span?.[0], span?.[1]]);
}

/** A board event with copies in Google calendars, drawn in their colours: the first fills it, a second is the other half of its mark; one with a colour of its own keeps it. */
export function withMarks(s: Shown, copies: string[], byKey: Map<string, GcalCalendar>, picks: CalColors | undefined): Shown {
  const marks = copies.filter((k) => byKey.has(k)).map((k) => calColor(byKey.get(k)!, picks));
  return marks.length && !s.ev.color ? { ...s, color: marks[0], meta: { ...s.meta, ...(marks[1] && marks[1] !== marks[0] ? { mark2: marks[1] } : {}) } } : s;
}

/** Whether an event is over, on the viewer's clock. */
export const isPast = (s: Shown, now: { today: string; min: number }) => (s.allDay ? s.last < now.today : s.end <= `${now.today}T${pad(Math.floor(now.min / 60))}:${pad(now.min % 60)}`);

/** A right-click on an event: its menu where the pointer was; one the board may not change opens its card. */
export function onEventMenu(s: Shown, readOnly?: boolean) {
  return (e: React.MouseEvent) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    if (!canChange(s)) return openEvent(s.id, e.currentTarget, s);
    eventMenu({ id: s.id, x: e.clientX, y: e.clientY, shown: s });
  };
}

/** An event's menu: Open, then a line and Delete, in red, last (the UI guide's order). */
export function EventMenu() {
  const menu = useAgendaUi((s) => s.menu);
  const shown = menu?.shown;
  if (!menu || !shown) return null;
  const close = () => eventMenu(null);
  return (
    <Pop at={new DOMRect(menu.x, menu.y, 0, 0)} onClose={close} width={200} className="agd-menu">
      <div className="ccal-menu-head">{eventTitle(shown.ev)}</div>
      <button className="menu-item" role="menuitem" onClick={() => (close(), openEvent(menu.id, document.querySelector(`[data-ag-ev="${CSS.escape(menu.id)}"]`), shown))}>
        <span className="menu-ico">
          <ExternalLink size={14} />
        </span>
        <span className="menu-label">Open</span>
      </button>
      {/* a layer item (step 6) opens on its own board, and is deleted there, never from the calendar */}
      {!shown.meta?.layer && (
        <>
          <div className="menu-sep" />
          <button className="menu-item is-danger" role="menuitem" onClick={() => (close(), void deleteShown(shown))}>
            <span className="menu-ico">
              <Trash2 size={14} />
            </span>
            <span className="menu-label">Delete</span>
          </button>
        </>
      )}
    </Pop>
  );
}
